import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateRange } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { Moment } from 'moment';
import { BehaviorSubject, distinctUntilChanged, filter, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IArticleRevenue } from '../../../../models/article-revenue.model';
import { IArticle } from '../../../../models/article.model';
import { IArticleRevenueDialogLayout } from '../../../../models/layout/article-revenue-dialog-layout.model';
import { ArticleRevenueFacade } from '../../../../services/facades/external-data/article-revenue-facade';
import { IArticleRevenueState } from '../../../../services/facades/external-data/article-revenue.state.model';
import { ISharedDataState } from '../../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../../services/facades/master-data/shared-data.facade';
import { ArticleLayoutService } from '../../../../services/layout/article-layout.service';
import { isDatefilterValid, isOverlapping, stringToMoment } from '../../../../utils/moment-utils';
import { AddRevenueDialogComponent } from './add-revenue-dialog/add-revenue-dialog.component';

@Component({
    templateUrl: './revenue-dialog.component.html',
    styleUrls: [ './revenue-dialog.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RevenueDialogComponent implements OnInit, AfterViewInit {

    @ViewChild(MatSort)
    public sort: MatSort;

    public articleRevenueState$: Observable<IArticleRevenueState>;
    public sharedDataState$: Observable<ISharedDataState>;
    public dataSource: MatTableDataSource<IArticleRevenue> = new MatTableDataSource<IArticleRevenue>([]);
    public layout: IArticleRevenueDialogLayout;
    public filterFormGroup$: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);

    constructor(
        private myMatDialogRef: MatDialogRef<RevenueDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public article: IArticle,
        private myArticleRevenueFacade: ArticleRevenueFacade,
        private myMatDialog: MatDialog,
        private myArticleLayoutService: ArticleLayoutService,
        private mySharedDataFacade: SharedDataFacade,
    ) {
    }

    public ngOnInit(): void {
        this.layout = this.myArticleLayoutService.getArticleRevenueDialogLayout();
        this.articleRevenueState$ = this.myArticleRevenueFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.articleRevenueState$
            .pipe(map((s: IArticleRevenueState) => s.articleRevenues))
            .subscribe((data: IArticleRevenue[]) => {
                this.dataSource.data = data;
            });
        void this.myArticleRevenueFacade.fetchArticleRevenues(this.article.id);
        this.dataSource.filterPredicate = (revenue: IArticleRevenue, _filterString: string): boolean => {
            const filterValues: { costAccountingType: number, validity: DateRange<Moment> } = this.filterFormGroup$.value.getRawValue();

            if (!isNullOrUndefined(filterValues.costAccountingType) && filterValues.costAccountingType !== revenue.costAccountingTypeId) {
                return false;
            }

            if (!isNullOrUndefined(filterValues.validity.start)
                && !isOverlapping(filterValues.validity, new DateRange<Moment>(stringToMoment(revenue.validFrom), stringToMoment(revenue.validTo)))) {
                return false;
            }

            return true;
        };
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
    }

    public onOpenAddDialog(): void {
        const dialog: MatDialogRef<AddRevenueDialogComponent> = this.myMatDialog.open(AddRevenueDialogComponent, {
            width: '600px',
            data: this.article,
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myArticleRevenueFacade.fetchArticleRevenues(this.article.id);
            }
        });
    }

    public onDeleteRevenue(revenue: IArticleRevenue): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            data: {
                message: 'app.dialog.article-revenue.confirm-delete.label',
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myArticleRevenueFacade.deleteArticleRevenue(this.article.id, revenue.id);
                void this.myArticleRevenueFacade.fetchArticleRevenues(this.article.id);
            }
        });
    }


    public close(): void {
        this.myMatDialogRef.close(false);
    }

    public onFormGroupChange(formGroup: FormGroup): void {

        formGroup.valueChanges.pipe(
            map((formData: { costAccountingType: string | number, validity: DateRange<Moment | string> }) => {
                const costAccountingType: number = typeof formData.costAccountingType === 'string' ? undefined : formData.costAccountingType;
                const start: Moment = typeof formData.validity.start === 'string' ? undefined : formData.validity.start;
                const end: Moment = typeof formData.validity.end === 'string' ? undefined : formData.validity.end;
                if (formData.costAccountingType === '') {
                    formGroup.controls[ 'costAccountingType' ].patchValue(undefined, { emitEvent: false });
                }
                if (formData.validity.start === '') {
                    formGroup.controls[ 'validity' ].get('start').patchValue(undefined, { emitEvent: false });
                }
                if (formData.validity.end === '') {
                    formGroup.controls[ 'validity' ].get('end').patchValue(undefined, { emitEvent: false });
                }
                return {
                    costAccountingType,
                    validity: new DateRange<Moment>(start, end),
                };
            }),
            filter((formData: { costAccountingType: number, validity: DateRange<Moment> }) => isDatefilterValid(formData.validity.start, formData.validity.end)),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        ).subscribe(() => {
            // Trigger the filterPredicate; The actual filterValue is not used
            this.dataSource.filter = 'dummyValue';
        });

        this.filterFormGroup$.next(formGroup);
    }
}
